import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Avatar,
  useMediaQuery,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Visibility,
  Edit,
  Delete,
  Close,
  Search,
  CloudUpload,
} from '@mui/icons-material';
import httpClient from '../../api/axios';

const ProductMaster = () => {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [formStatus, setFormStatus] = useState('Add Product');
  const [formData, setFormData] = useState({
    name: '',
    pricePerKg: '',
    description: '',
    isActive: true,
    productImg: null, // Changed from image to productImg to match API expectations
    text: '',
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Use ref for file input
  const fileInputRef = useRef(null);
  const fileInputRefCover = useRef(null);

  // Theme and responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await httpClient.get(`/api/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      showNotification('Failed to load products', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Toast notification alternative using the Snackbar/Alert
  const toast = {
    error: (message) => {
      showNotification(message, 'error');
    },
    success: (message) => {
      showNotification(message, 'success');
    },
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const parsedValue =
      name === 'isActive' ? value === 'true' : value;
    setFormData({
      ...formData,
      [name]: parsedValue,
    });
  };

  const handleFileChange = async (event) => {
    event.preventDefault();
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    setUploading(true);

    // Create a local preview URL
    const localPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(localPreviewUrl);

    const form = new FormData();
    form.append('image', file);

    try {
      const res = await httpClient.post(`/api/file_upload`, form, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.data && res.data.path) {
        // Update formData with the server path for the image
        setFormData((prevFormData) => ({
          ...prevFormData,
          productImg: res.data.path, // Use productImg here to match the API expectation
        }));
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Invalid response from server');
        // Revoke the object URL if upload failed
        URL.revokeObjectURL(localPreviewUrl);
        setPreviewUrl(null);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(
        'Error uploading image: ' +
          (error.response?.data?.message || error.message)
      );
      // Revoke the object URL if upload failed
      URL.revokeObjectURL(localPreviewUrl);
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleAddClick = () => {
    setFormStatus('Add Product');
    setFormData({
      name: '',
      pricePerKg: '',
      description: '',
      isActive: true,
      productImg: null,
      text: '',
    });
    setImagePreview(null);
    setPreviewUrl(null);
    setErrors({});
    setOpen(true);
  };

  const handleEdit = (product) => {
    setFormStatus('Update Product');
    setFormData({
      _id: product._id,
      name: product.name,
      pricePerKg: product.pricePerKg,
      description: product.description,
      isActive: product.isActive,
      productImg: product.productImg, // Changed from image to productImg
      text: product.text || '',
    });
    setImagePreview(product.imageUrl);
    setPreviewUrl(product.imageUrl);
    setErrors({});
    setOpen(true);
  };

  const handleView = (product) => setViewProduct(product);

  const handleDelete = (product) => {
    setProductToDelete(product);
    setOpenDeleteDialog(true);
  };

  const handleSearchChange = (e) => {
    const value = e?.target?.value || '';
    setSearchQuery(value.toLowerCase());
    setPage(0);
  };

  const handleButtonClickCover = () => {
    fileInputRefCover.current.click();
  };

  const handleConfirmDelete = async () => {
    try {
      await httpClient.delete(`/api/products/${productToDelete._id}`);
      setProducts(
        products.filter((p) => p._id !== productToDelete._id)
      );
      showNotification('Product deleted successfully');
    } catch (error) {
      console.error('Error deleting product:', error);
      showNotification('Failed to delete product', 'error');
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const validateForm = () => {
    let err = {};
    if (!formData.name) err.name = 'Name is required';
    if (!formData.pricePerKg)
      err.pricePerKg = 'Price per kg is required';
    else if (isNaN(formData.pricePerKg) || formData.pricePerKg <= 0)
      err.pricePerKg = 'Please enter a valid price';
    if (!formData.description)
      err.description = 'Description is required';
    if (formStatus === 'Add Product' && !formData.productImg)
      err.productImg = 'Product image is required';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const productData = {
        name: formData.name,
        pricePerKg: parseFloat(formData.pricePerKg),
        description: formData.description,
        isActive: formData.isActive,
        productImg: formData.productImg, // Changed from image to productImg
        text: formData.text,
      };

      if (formStatus === 'Update Product') {
        // Update existing product
        productData._id = formData._id;
        await httpClient.put(`/api/products`, productData);
        showNotification('Product updated successfully');
      } else {
        // Create new product
        await httpClient.post(`/api/products`, productData);
        showNotification('Product added successfully');
      }
      // Refresh products
      fetchProducts();
      setOpen(false);
    } catch (error) {
      console.error('Error saving product:', error);
      showNotification(
        `Failed to ${
          formStatus === 'Update Product' ? 'update' : 'add'
        } product`,
        'error'
      );
    }
  };

  const filteredProducts = products.filter(
    (p) =>
      (p.name?.toLowerCase() || '').includes(searchQuery) ||
      (p.description?.toLowerCase() || '').includes(searchQuery)
  );

  const paginatedProducts = filteredProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Hide certain columns on mobile
  const getVisibleColumns = () => {
    if (isMobile) {
      return ['image', 'name', 'actions'];
    } else if (isTablet) {
      return ['image', 'name', 'price', 'status', 'actions'];
    } else {
      return [
        'image',
        'name',
        'price',
        'description',
        'status',
        'created',
        'actions',
      ];
    }
  };

  const visibleColumns = getVisibleColumns();

  return (
    <div className='flex justify-center items-start min-h-screen bg-gray-100 p-2 md:p-4 w-full'>
      <Paper className='w-full max-w-6xl p-3 md:p-6 shadow-lg'>
        <Typography
          variant='h5'
          className='text-center font-bold mb-4'
        >
          Flour Product Master
        </Typography>
        <Box display='flex' gap={2} mb={3}>
          <TextField
            label='Search Products'
            variant='outlined'
            fullWidth
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <Search />
                </IconButton>
              ),
            }}
          />
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {visibleColumns.includes('image') && (
                  <TableCell width='80'>
                    <strong>Image</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('name') && (
                  <TableCell>
                    <strong>Name</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('price') && (
                  <TableCell>
                    <strong>Price (₹)</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('description') && (
                  <TableCell>
                    <strong>Description</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('status') && (
                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('created') && (
                  <TableCell>
                    <strong>Created At</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('actions') && (
                  <TableCell>
                    <strong>Actions</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    align='center'
                  >
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 1, display: 'inline' }}>
                      Loading products...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedProducts.length > 0 ? (
                paginatedProducts.map((p) => (
                  <TableRow key={p._id}>
                    {visibleColumns.includes('image') && (
                      <TableCell>
                        <Avatar
                          src={
                            p.productImg || '/placeholder-product.png'
                          }
                          alt={p.name}
                          sx={{ width: 50, height: 50 }}
                          variant='rounded'
                        />
                      </TableCell>
                    )}
                    {visibleColumns.includes('name') && (
                      <TableCell>{p.name}</TableCell>
                    )}
                    {visibleColumns.includes('price') && (
                      <TableCell>₹{p.pricePerKg}</TableCell>
                    )}
                    {visibleColumns.includes('description') && (
                      <TableCell>
                        {isMobile || isTablet
                          ? p.description?.length > 50
                            ? `${p.description.substring(0, 50)}...`
                            : p.description
                          : p.description}
                      </TableCell>
                    )}
                    {visibleColumns.includes('status') && (
                      <TableCell>
                        {p.isActive ? 'Active' : 'Inactive'}
                      </TableCell>
                    )}
                    {visibleColumns.includes('created') && (
                      <TableCell>
                        {new Date(p.createdAt).toLocaleDateString()}
                      </TableCell>
                    )}
                    {visibleColumns.includes('actions') && (
                      <TableCell>
                        <Box
                          sx={{ display: 'flex', flexWrap: 'nowrap' }}
                        >
                          <IconButton
                            onClick={() => handleView(p)}
                            className='!bg-amber-500'
                            size='small'
                            sx={{ mr: 1 }}
                          >
                            <Visibility
                              fontSize={isMobile ? 'small' : 'medium'}
                            />
                          </IconButton>
                          <IconButton
                            onClick={() => handleEdit(p)}
                            className='!bg-amber-500'
                            size='small'
                            sx={{ mr: 1 }}
                          >
                            <Edit
                              fontSize={isMobile ? 'small' : 'medium'}
                            />
                          </IconButton>
                          {/* <IconButton
                            onClick={() => handleDelete(p)}
                            className='!bg-amber-500'
                            size='small'
                          >
                            <Delete
                              fontSize={isMobile ? 'small' : 'medium'}
                            />
                          </IconButton> */}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    align='center'
                  >
                    No Products Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component='div'
            count={filteredProducts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) =>
              setRowsPerPage(parseInt(e.target.value, 10))
            }
          />
        </TableContainer>

        <div className='flex justify-center mt-4'>
          <Button
            onClick={handleAddClick}
            variant='contained'
            className='!bg-amber-500 !text-white'
          >
            Add Product
          </Button>
        </div>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {formStatus}
          <IconButton
            edge='end'
            onClick={() => setOpen(false)}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label='Flour Name'
            fullWidth
            margin='normal'
            name='name'
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />
          <TextField
            label='Price (₹)'
            fullWidth
            margin='normal'
            name='pricePerKg'
            type='number'
            value={formData.pricePerKg}
            onChange={handleChange}
            error={!!errors.pricePerKg}
            helperText={errors.pricePerKg}
          />
          <TextField
            label='Description'
            fullWidth
            margin='normal'
            name='description'
            value={formData.description}
            onChange={handleChange}
            error={!!errors.description}
            helperText={errors.description}
            multiline
            rows={3}
          />
          <FormControl fullWidth margin='normal'>
            <InputLabel>Status</InputLabel>
            <Select
              name='isActive'
              value={formData.isActive.toString()}
              onChange={handleChange}
            >
              <MenuItem value='true'>Active</MenuItem>
              <MenuItem value='false'>Inactive</MenuItem>
            </Select>
          </FormControl>

          {/* Image Upload Field with useRef */}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label='Text'
              fullWidth
              value={formData.text}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  text: e.target.value,
                }))
              }
            />
            <Box>
              {previewUrl ? (
                <Avatar
                  src={previewUrl}
                  variant='rounded'
                  sx={{ width: '100%', height: 200, mb: 1 }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: 200,
                    bgcolor: '#F0F0F0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 1,
                    border: '1px dashed #ccc',
                    borderRadius: 1,
                  }}
                >
                  <Typography color='textSecondary'>
                    No Image Selected
                  </Typography>
                </Box>
              )}
              <input
                type='file'
                accept='image/*'
                style={{ display: 'none' }}
                ref={fileInputRefCover}
                onChange={handleFileChange}
              />
              <Button
                variant='contained'
                color='primary'
                onClick={handleButtonClickCover}
                fullWidth
                disabled={uploading}
                startIcon={
                  uploading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CloudUpload />
                  )
                }
              >
                {uploading ? 'Uploading...' : 'Browse Image'}
              </Button>
              {errors.productImg && (
                <Typography
                  color='error'
                  variant='caption'
                  sx={{ mt: 0.5 }}
                >
                  {errors.productImg}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSave}
            variant='contained'
            className='!bg-amber-500 !text-white'
            style={{ width: isMobile ? '100%' : '200px' }}
            disabled={uploading}
          >
            {formStatus}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={!!viewProduct}
        onClose={() => setViewProduct(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>View Product</DialogTitle>
        <DialogContent>
          {viewProduct && (
            <div className='space-y-2'>
              {/* Product Image */}
              {viewProduct.imageUrl && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <img
                    src={viewProduct.imageUrl}
                    alt={viewProduct.name}
                    style={{
                      maxHeight: '200px',
                      maxWidth: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              )}
              <Typography variant='h6'>
                Name: {viewProduct.name}
              </Typography>
              <Typography>
                Price per Kg: ₹{viewProduct.pricePerKg}
              </Typography>
              <Typography>
                Description: {viewProduct.description}
              </Typography>
              <Typography>
                Status: {viewProduct.isActive ? 'Active' : 'Inactive'}
              </Typography>
              <Typography>
                Created At:{' '}
                {new Date(viewProduct.createdAt).toLocaleDateString()}
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewProduct(null)}
            className='!bg-amber-500 !text-white'
            fullWidth={isMobile}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this product?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            color='secondary'
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color='primary'>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ProductMaster;
